import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { AlertService, AlertType } from '../../services/alert.service';
import { ControleEstoqueService } from '../../services/apis/produto/controle-estoque.service';
import { ControleEstoqueDetalheSeriesRequest } from '../../services/requests/produto/controle-estoque-detalhe-series.request';

export interface SerieInventarioModalData {
  idInventarioItem?: number;
  idProduto: number;
  idLocalizacaoEstoque: number;
  nomeProduto: string;
  skuProduto?: string;
  saldoSistema: number;
  series: SerieInventarioModalItem[];
  isReadOnly?: boolean;
}

export interface SerieInventarioModalItem {
  idInventarioItemSerie?: number;
  idSerie?: number;
  codigoSerie: string;
  saldoSistema: number;
  saldoContado: number;
  isExistente?: boolean;
  isRemovido?: boolean;
}

@Component({
  selector: 'app-serie-inventario-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './serie-inventario-modal.component.html',
  styleUrl: './serie-inventario-modal.component.css',
})
export class SerieInventarioModalComponent implements OnInit, AfterViewInit {
  public nomeProduto: string = '';
  public skuProduto: string = '';
  public saldoSistema: number = 0;
  public isReadOnly: boolean = false;
  public isLoading: boolean = false;
  public erroCarga: string | null = null;

  public dataSource = new MatTableDataSource<SerieInventarioModalItem>([]);
  public displayedColumns: string[] = ['codigoSerie', 'saldoSistema', 'acoes'];

  public novoCodigoSerie: string = '';

  public isNarrow = window.innerWidth <= 700;
  @HostListener('window:resize') onResize(): void {
    this.isNarrow = window.innerWidth <= 700;
  }

  @ViewChild('inputSerie') inputSerie?: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SerieInventarioModalData,
    public dialogRef: MatDialogRef<SerieInventarioModalComponent>,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.nomeProduto = this.data.nomeProduto;
    this.skuProduto = this.data.skuProduto ?? '';
    this.saldoSistema = this.data.saldoSistema;
    this.isReadOnly = this.data.isReadOnly ?? false;

    if (this.isReadOnly) {
      this.displayedColumns = ['codigoSerie', 'saldoSistema'];
    }

    this.carregarSeriesExistentes();
  }

  ngAfterViewInit(): void {
    if (!this.isReadOnly) this.focarInput();
  }

  private focarInput(): void {
    setTimeout(() => this.inputSerie?.nativeElement.focus(), 0);
  }

  private carregarSeriesExistentes(): void {
    this.isLoading = true;
    this.erroCarga = null;

    const request: ControleEstoqueDetalheSeriesRequest = {
      idLocalizacaoEstoque: this.data.idLocalizacaoEstoque,
      idStatusFiltro: 1,
    };

    this.controleEstoqueService.getSeries(this.data.idProduto, request, 0).subscribe({
      next: (resp) => {
        const existentes: SerieInventarioModalItem[] = (resp?.list ?? [])
          .filter(s => (s.saldoDisponivel ?? 0) > 0)
          .map(s => ({
            idSerie: s.idSerie,
            codigoSerie: s.numeroSerie,
            saldoSistema: s.saldoDisponivel,
            saldoContado: s.saldoDisponivel,
            isExistente: true,
          }));

        const salvosPorIdSerie = new Map<number, SerieInventarioModalItem>();
        const salvosSemIdSerie: SerieInventarioModalItem[] = [];
        for (const s of this.data.series ?? []) {
          if (s.idSerie && s.idSerie > 0) salvosPorIdSerie.set(s.idSerie, s);
          else salvosSemIdSerie.push(s);
        }

        for (const linha of existentes) {
          if (linha.idSerie != null && salvosPorIdSerie.has(linha.idSerie)) {
            const salvo = salvosPorIdSerie.get(linha.idSerie)!;
            linha.idInventarioItemSerie = salvo.idInventarioItemSerie;
            linha.saldoContado = salvo.saldoContado;
            if (salvo.saldoContado === 0) linha.isRemovido = true;
            salvosPorIdSerie.delete(linha.idSerie);
          }
        }

        const orfaos: SerieInventarioModalItem[] = [];
        salvosPorIdSerie.forEach(s => orfaos.push({ ...s, isExistente: false, saldoSistema: 0 }));
        for (const s of salvosSemIdSerie) {
          orfaos.push({ ...s, isExistente: false, saldoSistema: 0 });
        }

        this.dataSource.data = [...existentes, ...orfaos];
        this.isLoading = false;
      },
      error: () => {
        this.erroCarga = 'Não foi possível carregar séries existentes. Apenas inclusão manual disponível.';
        this.dataSource.data = (this.data.series ?? []).map(s => ({
          ...s,
          isExistente: !!s.idSerie && s.idSerie > 0,
        }));
        this.isLoading = false;
      },
    });
  }

  public get totalContado(): number {
    // 1 série = 1 unidade: cada série presente (não removida) conta como 1.
    return this.dataSource.data.filter(s => !s.isRemovido).length;
  }

  public adicionarSerie(): void {
    const codigo = this.novoCodigoSerie.trim();
    if (!codigo) return;

    const duplicado = this.dataSource.data.some(s =>
      (s.codigoSerie || '').trim().toLowerCase() === codigo.toLowerCase());
    if (duplicado) return;

    this.dataSource.data = [...this.dataSource.data, {
      codigoSerie: codigo,
      saldoSistema: 0,
      saldoContado: 1,
      isExistente: false,
    }];
    this.novoCodigoSerie = '';
    this.focarInput();
  }

  public removerSerie(index: number): void {
    const lista = [...this.dataSource.data];
    const row = lista[index];
    if (row?.isExistente) {
      row.saldoContado = 0;
      row.isRemovido = true;
      this.dataSource.data = lista;
    } else {
      lista.splice(index, 1);
      this.dataSource.data = lista;
    }
  }

  public desfazerRemocao(index: number): void {
    const lista = [...this.dataSource.data];
    const row = lista[index];
    if (row?.isExistente && row.isRemovido) {
      row.saldoContado = row.saldoSistema;
      row.isRemovido = false;
      this.dataSource.data = lista;
    }
  }

  public isSalvando = false;

  public confirmar(): void {
    if (this.isSalvando) return;

    // Série digitada nova (sem id): cria/obtém na API Produto AGORA — daí em diante só o id
    // trafega (Suprimentos não guarda mais snapshot de código).
    const novas = this.dataSource.data.filter(s => !s.idSerie);
    if (novas.length === 0) {
      this.fechar();
      return;
    }

    this.isSalvando = true;
    const chamadas = novas.map(s =>
      this.controleEstoqueService.obterOuCriarSerie({
        idProduto: this.data.idProduto,
        codigoSerie: s.codigoSerie.trim(),
      }));

    forkJoin(chamadas).subscribe({
      next: respostas => {
        this.isSalvando = false;
        const falha = respostas.find(resp => resp?.isError || !resp?.idSerie);
        if (falha) {
          this.alertService.show('Erro', falha.errorDescription || 'Não foi possível registrar a série informada.', AlertType.error);
          return; // mantém a digitação do usuário no modal
        }
        respostas.forEach((resp, i) => (novas[i].idSerie = resp.idSerie));
        this.fechar();
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Não foi possível registrar as séries informadas.', AlertType.error);
      },
    });
  }

  private fechar(): void {
    // 1 série = 1 unidade: presente conta 1, removida conta 0.
    const series: SerieInventarioModalItem[] = this.dataSource.data.map(s => ({
      ...s,
      saldoContado: s.isRemovido ? 0 : 1,
    }));
    this.dialogRef.close({ confirmed: true, series });
  }

  public cancelar(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
